import React, {useContext, useState, useMemo} from 'react';
import { Outlet, Link } from "react-router-dom";
import helpers from '../../services/helpers';
import images from '../../services/images';
import { MdHandyman, MdManageAccounts } from "react-icons/md";
import { PiToolboxFill } from "react-icons/pi";
import { MdPointOfSale } from "react-icons/md";
import { FaMoneyBillTransfer } from "react-icons/fa6";
import ProfileContext from '../../services/profileContext';
import ProtectedComponent from '../../services/protectedComponent';
import { GrCloudComputer } from "react-icons/gr";

export default function SideMenu({menu}){
    const {
        account
    } = useContext(ProfileContext);
    const base_root = window.location.host
    const [openGroup, setOpenGroup] = useState(""); // Tracks which dropdown is toggled

    // Toggle function for parent menus
    const handleToggle = (groupName) => {
        setOpenGroup(openGroup === groupName ? "" : groupName);
    };

    // This determines if a menu section should have the "open" classes
    const getGroupClass = (groupName, subMenuIds) => {
        // 1. Is it open because we clicked it?
        const isManuallyOpen = openGroup === groupName;
        // 2. Is it open because our current URL (menu prop) is inside this group?
        const isUrlOpen = subMenuIds.includes(menu);

        return (isManuallyOpen || isUrlOpen) ? "nav-item menu-is-opening menu-open" : "nav-item";
    };

    // This forces the dropdown to show/hide
    const getTreeviewStyle = (groupName, subMenuIds) => {
        const isOpen = openGroup === groupName || subMenuIds.includes(menu);
        return { display: isOpen ? 'block' : 'none' };
    };

    // 1. Define the IDs for each group
    const inventoryIds = ['manage-item', 'manage-item-form', 'item-brand', 'variant-types', 'manage-inventory-adjustment', 'item-category', 'featured-products'];
    const salesIds = ['manage-sales', 'manage-quotations', 'manage-sales-form', 'manage-shipments', 'manage-customers', 'couriers', 'manage-invoices', 'manage-payment-receives', 'manage-sales-return'];
    const purchaseIds = ['manage-purchases', 'manage-purchases-form', 'manage-purchase-receives', 'manage-vendors', 'manage-bills', 'manage-payment-made'];
    const serviceIds = ['manage-service-requests', 'manage-product-units', 'manage-service-schedule'];
    const accountIds = ['users', 'roles', 'permissions'];
    const employeeIds = ['employees'];
    const reportIds = ['reports'];
    const settingIds = ['settings'];
    const toolsIds = ['qr-range-generator'];
    const cashFlowIds = ['cashflow-accounts','cashflow-transactions','cashflow-categories'];
    const posIds = ['pos-cashier', 'pos-terminals'];

    // 2. Build the menuState object (Ensures no "undefined" errors)
    const menuState = {
        login: {
            active: menu === 'login' ? 'active' : ''
        },
        dashboard: {
            groupClass: getGroupClass("dashboard", ['home', 'customer-dashboard']),
            style: getTreeviewStyle("dashboard", ['home', 'customer-dashboard']),
            active: ['home', 'customer-dashboard'].includes(menu) ? 'active' : ''
        },
        inventory: {
            groupClass: getGroupClass("inventory", inventoryIds),
            style: getTreeviewStyle("inventory", inventoryIds),
            active: inventoryIds.includes(menu) ? 'active' : ''
        },
        sales: {
            groupClass: getGroupClass("sales", salesIds),
            style: getTreeviewStyle("sales", salesIds),
            active: salesIds.includes(menu) ? 'active' : ''
        },
        purchases: {
            groupClass: getGroupClass("purchases", purchaseIds),
            style: getTreeviewStyle("purchases", purchaseIds),
            active: purchaseIds.includes(menu) ? 'active' : ''
        },
        service: {
            groupClass: getGroupClass("service", serviceIds),
            style: getTreeviewStyle("service", serviceIds),
            active: serviceIds.includes(menu) ? 'active' : ''
        },
        accounts: {
            groupClass: getGroupClass("accounts", accountIds),
            style: getTreeviewStyle("accounts", accountIds),
            active: accountIds.includes(menu) ? 'active' : ''
        },
        employees: {
            active: employeeIds.includes(menu) ? 'active' : ''
        },
        reports: {
            active: reportIds.includes(menu) ? 'active' : ''
        },
        settings: {
            active: settingIds.includes(menu) ? 'active' : ''
        },
        tools: {
            groupClass: getGroupClass("tools", toolsIds),
            style: getTreeviewStyle("tools", toolsIds),
            active: toolsIds.includes(menu) ? 'active' : ''
        },
        cashflow: {
            groupClass: getGroupClass("cashflow", cashFlowIds),
            style: getTreeviewStyle("cashflow", cashFlowIds),
            active: cashFlowIds.includes(menu) ? 'active' : ''
        },
        pos: {
            groupClass: getGroupClass("pos", posIds),
            style: getTreeviewStyle("pos", posIds),
            active: posIds.includes(menu) ? 'active' : ''
        },
    };

    return (
        <>
        {/* Main Sidebar Container */}
        <aside className="main-sidebar elevation-4 sidebar-light-dark">
        {/* <aside className="main-sidebar sidebar-dark-light elevation-4"> */}
            {/* Brand Logo */}
            <Link className="brand-link d-flex align-items-center" to="/">
                <img src={images.topventory_icon} alt="Icon"  className="brand-image" />
                <img className='brand-text' src={images.topventory_text_only} alt="Logo" />
                {/* <div className='brand-text'>WV SERVICES</div> */}
                {/* <small className='ml-1'><sup>BETA</sup></small> */}
            </Link>

            {/* Sidebar */}
            
            <div className="sidebar">
                {/* Sidebar Menu */}
                <nav className="mt-2">
                    { !helpers.isAuthenticated() && (
                        <ul className="nav nav-pills nav-sidebar flex-column nav-collapse-hide-child nav-child-indent nav-compact" role="menu">
                            <li className="nav-item">
                                <Link 
                                    to="/login" 
                                    className={`nav-link ${menu === 'login' ? 'active' : ''}`}
                                >
                                    <i className="fas fa-sign-in-alt mr-3"></i>
                                    <p>Login</p>
                                </Link>
                            </li>
                        </ul>
                    )}
                    
                    { 
                        helpers.isAuthenticated() ?
                        <ul className="nav nav-pills nav-sidebar flex-column nav-collapse-hide-child nav-child-indent nav-compact" data-widget="treeviews" role="menu" data-accordion="false">
                            <li className={getGroupClass("dashboard", ['home', 'customer-dashboard'])}>
                                <a 
                                    href="#" 
                                    className={`nav-link nav-link-main ${['home', 'customer-dashboard'].includes(menu) ? 'active' : ''}`} 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleToggle("dashboard");
                                    }}
                                >
                                    <i className="nav-icon fas fa-tachometer-alt mr-2" />
                                    <p>
                                        Dashboard 
                                        <i className="right fas fa-angle-left" />
                                    </p>
                                </a>
                                <ul className="nav nav-treeview" style={getTreeviewStyle("dashboard", ['home', 'customer-dashboard'])}>
                                    <li className="nav-item">
                                        <Link to="/dashboard" className={`nav-link ${menu === 'home' ? 'active' : ''}`}>
                                            <p className='ml-3'>Home</p>
                                        </Link>
                                    </li>

                                    <ProtectedComponent account={account} requiredPermissions={["superAdmin","serviceHubClientMap"]}>
                                        <li className="nav-item">
                                            <Link to="/customer-map" className={`nav-link ${menu === 'customer-dashboard' ? 'active' : ''}`}>
                                                <p className='ml-3'>Customer</p>
                                            </Link>
                                        </li>
                                    </ProtectedComponent>
                                </ul>
                            </li>

                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","inventoryView", "inventoryCreate", "inventoryEdit", "inventoryDelete","inventoryAdjustView","inventoryAdjustCreate","inventoryAdjustEdit","inventoryAdjustDelete"]}>
                                <li className={menuState.inventory.groupClass}>
                                    <a href="#" className={`nav-link nav-link-main ${menuState.inventory.active}`} 
                                    onClick={(e) => { e.preventDefault(); handleToggle("inventory"); }}>
                                        <i className="nav-icon fas fa-tags mr-2" />
                                        <p>Inventory <i className="right fas fa-angle-left" /></p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.inventory.style}>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","inventoryView", "inventoryCreate", "inventoryEdit", "inventoryDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/item/list" className={`nav-link ${menu === 'manage-item' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Items</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","inventoryAdjustView","inventoryAdjustCreate","inventoryAdjustEdit","inventoryAdjustDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/inventory-adjustment" className={`nav-link ${menu === 'manage-inventory-adjustment' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Inventory Adjustment</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","inventoryView", "inventoryCreate", "inventoryEdit", "inventoryDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/featured-items" className={`nav-link ${menu === 'featured-products' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Featured Items</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent>

                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","quotationCreate", "quotationEdit", "quotationDelete", "quotationCancel","salesOrderCreate", "salesOrderEdit", "salesOrderDelete", "salesOrderCancel", "invoiceView", "invoiceCreate", "invoiceEdit", "invoiceDelete", "invoiceCancel", "paymentReceiveView", "paymentReceiveCreate","paymentReceiveEdit","paymentReceiveDelete","customerView","customerCreate","customerEdit","customerDelete","shipmentView","shipmentCreate","shipmentEdit","shipmentDelete","salesReturnView","salesReturnCreate","salesReturnEdit","salesReturnDelete"]}>
                                <li className={menuState.sales.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`pointer nav-link nav-link-main ${menuState.sales.active}`}
                                        onClick={(e) => { e.preventDefault(); handleToggle("sales"); }}
                                    >
                                        <i className="nav-icon fas fa-shopping-cart mr-2"></i>
                                        <p>
                                            {helpers.getSettingValue(account?.settings, 'sales_group_module_menu_label', 'Sales')} 
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    
                                    <ul className="nav nav-treeview" style={menuState.sales.style}>
                                        
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","quotationCreate", "quotationEdit", "quotationDelete", "quotationCancel"]}>
                                            <li className="nav-item">
                                                <Link className={`nav-link ${menu === 'manage-quotations' ? 'active' : ''}`} to="/quotations/list">
                                                    <p className='ml-3'>{helpers.getSettingValue(account?.settings, 'quotation_menu_label', 'Quotations')}</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","salesOrderCreate", "salesOrderEdit", "salesOrderDelete", "salesOrderCancel"]}>
                                            <li className="nav-item">
                                                <Link className={`nav-link ${menu === 'manage-sales' ? 'active' : ''}`} to="/sales/list">
                                                    <p className='ml-3'>{helpers.getSettingValue(account?.settings, 'sales_order_menu_label', 'Sales Order')}</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","invoiceView", "invoiceCreate", "invoiceEdit", "invoiceDelete"]}>
                                            <li className="nav-item">
                                                <Link className={`nav-link ${menu === 'manage-invoices' ? 'active' : ''}`} to="/invoices/list">
                                                    <p className='ml-3'>{helpers.getSettingValue(account?.settings, 'invoice_menu_label', 'Sales Invoice')}</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","paymentReceiveView", "paymentReceiveCreate","paymentReceiveEdit","paymentReceiveDelete"]}>
                                            <li className="nav-item">
                                                <Link className={`nav-link ${menu === 'manage-payment-receives' ? 'active' : ''}`} to="/payment-receives/list">
                                                    <p className='ml-3'>Payment Receives</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","customerView","customerCreate","customerEdit","customerDelete"]}>
                                            <li className="nav-item">
                                                <Link className={`nav-link ${menu === 'manage-customers' ? 'active' : ''}`} to="/customers/list">
                                                    <p className='ml-3'>Customers</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","shipmentView","shipmentCreate","shipmentEdit","shipmentDelete"]}>
                                            <li className="nav-item">
                                                <Link className={`nav-link ${menu === 'manage-shipments' ? 'active' : ''}`} to="/shipments/list">
                                                    <p className='ml-3'>Shipments</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","salesReturnView","salesReturnCreate","salesReturnEdit","salesReturnDelete"]}>
                                            <li className="nav-item">
                                                <Link className={`nav-link ${menu === 'manage-sales-return' ? 'active' : ''}`} to="/sales-returns/list">
                                                    <p className='ml-3'>Sales Return</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        
                                    </ul>
                                </li>
                            </ProtectedComponent>

                            {/* <ProtectedComponent account={account} requiredPermissions={["superAdmin","posView", "posCreate", "posEdit", "posDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.pos.active}`} to="/pos">
                                        <MdPointOfSale size={25} className='mr-2' />
                                        <p>BentaPro</p>
                                    </Link>
                                </li>
                            </ProtectedComponent> */}
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","posCashierView", "posCashierCreate", "posCashierEdit", "posCashierDelete", "posTerminalView", "posTerminalCreate", "posTerminalEdit", "posTerminalDelete"]}>
                                <li className={menuState.pos.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.pos.active}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("pos");
                                        }}
                                    >
                                        <MdPointOfSale size={25} className='mr-2'/>
                                        <p>
                                            POS
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.pos.style}>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","posCashierView", "posCashierCreate", "posCashierEdit", "posCashierDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/pos/cashier" className={`nav-link ${menu === 'pos-cashier' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Cashier</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","posTerminalView", "posTerminalCreate", "posTerminalEdit", "posTerminalDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/pos/terminals" className={`nav-link ${menu === 'pos-terminals' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Terminals</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent>

                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","purchaseOrderView", "purchaseOrderCreate", "purchaseOrderEdit", "purchaseOrderDelete", "purchaseOrderCancel", "billView", "billCreate", "billEdit", "billDelete", "billCancel", "purchaseReceiveView","purchaseReceiveCreate","purchaseReceiveEdit","purchaseReceiveDelete","vendorView","vendorCreate","vendorEdit","vendorDelete","paymentMadeView","paymentMadeCreate","paymentMadeEdit","paymentMadeDelete"]}>
                                <li className={menuState.purchases.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.purchases.active}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("purchases");
                                        }}
                                    >
                                        <i className="nav-icon fas fa-shopping-bag mr-2" />
                                        <p>
                                            {helpers.getSettingValue(account?.settings, 'purchases_group_module_menu_label', 'Purchases')} 
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.purchases.style}>
                                        
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","purchaseOrderView", "purchaseOrderCreate", "purchaseOrderEdit", "purchaseOrderDelete", "purchaseOrderCancel"]}>
                                            <li className="nav-item">
                                                <Link to="/purchases/list" className={`nav-link ${menu === 'manage-purchases' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Purchase Orders</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","billView", "billCreate", "billEdit", "billDelete", "billCancel"]}>
                                            <li className="nav-item">
                                                <Link to="/bills/list" className={`nav-link ${menu === 'manage-bills' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Bills</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","paymentMadeView","paymentMadeCreate","paymentMadeEdit","paymentMadeDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/payment-made/list" className={`nav-link ${menu === 'manage-payment-made' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Payment Makes</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","vendorView","vendorCreate","vendorEdit","vendorDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/vendors/list" className={`nav-link ${menu === 'manage-vendors' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Vendors</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","purchaseReceiveView","purchaseReceiveCreate","purchaseReceiveEdit","purchaseReceiveDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/purchase-receives/list" className={`nav-link ${menu === 'manage-purchase-receives' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Purchase Receives</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                    </ul>
                                </li>
                            </ProtectedComponent>
                            
                            {/* <ProtectedComponent account={account} requiredPermissions={["superAdmin","serviceHubView", "serviceHubViewAssgined", "serviceHubCreate", "serviceHubEdit", "serviceHubDelete", "serviceHubSchedule", "serviceHubAssess", "serviceHubAccept", "productUnitView", "productUnitCreate", "productUnitEdit","productUnitDelete", "generateQR"]}>
                                <li className={menuState.service.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.service.active}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("service");
                                        }}
                                    >
                                        <MdHandyman size={24} className='mr-2' />
                                        <p>
                                            Service Hub 
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.service.style}>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","serviceHubView", "serviceHubCreate", "serviceHubEdit", "serviceHubDelete", "serviceHubSchedule", "serviceHubAssess", "serviceHubAccept"]}>
                                            <li className="nav-item">
                                                <Link to="/service-requests/list" className={`nav-link ${menu === 'manage-service-requests' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Service Request</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","serviceHubViewAssgined", "serviceHubCreate", "serviceHubEdit", "serviceHubDelete", "serviceHubSchedule", "serviceHubAssess", "serviceHubAccept"]}>
                                            <li className="nav-item">
                                                <Link to="/service-requests/schedule" className={`nav-link ${menu === 'manage-service-schedule' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Schedules</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","productUnitView", "productUnitCreate", "productUnitEdit","productUnitDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/product-units/list" className={`nav-link ${menu === 'manage-product-units' ? 'active' : ''}`}>
                                                    <p className='ml-3'>PolarCare Units</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent> */}

                            {/* Cash flows */}
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","cashFlowAccountView", "cashFlowAccountCreate", "cashFlowAccountEdit", "cashFlowAccountDelete", "cashFlowCategoryView", "cashFlowCategoryCreate", "cashFlowCategoryEdit", "cashFlowCategoryDelete", "cashFlowTransactionView", "cashFlowTransactionCreate", "cashFlowCTransactionEdit", "cashFlowTransactionDelete"]}>
                                <li className={menuState.cashflow.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.cashflow.active}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("cashflow");
                                        }}
                                    >
                                        <FaMoneyBillTransfer size={25} className='mr-2'/>
                                        <p>
                                            Cash Flow
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.cashflow.style}>
                                        {/* <ProtectedComponent account={account} requiredPermissions={["superAdmin","cashFlowAccountView", "cashFlowAccountCreate", "cashFlowAccountEdit", "cashFlowAccountDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/cashflow/accounts" className={`nav-link ${menu === 'cashflow-accounts' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Accounts</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent> */}
                                        {/*  */}
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","cashFlowCategoryView", "cashFlowCategoryCreate", "cashFlowCategoryEdit", "cashFlowCategoryDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/cashflow/categories" className={`nav-link ${menu === 'cashflow-categories' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Categories</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","cashFlowTransactionView", "cashFlowTransactionCreate", "cashFlowTransactionEdit", "cashFlowTransactionDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/cashflow/transactions" className={`nav-link ${menu === 'cashflow-transactions' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Transactions</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent>

                            {/* Employees */}
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","employeeView", "employeeCreate", "employeeEdit", "employeeDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.employees.active}`} to="/hrms/employees/list">
                                        <i className="nav-icon fas fa-users mr-2" />
                                        <p>Employees</p>
                                    </Link>
                                </li>
                            </ProtectedComponent>

                            {/* Accounts Group */}
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","acount_profileView", "accountCreate", "accountEdit", "accountDelete","userView", "userCreate", "userEdit","userDelete","roleView", "roleCreate", "roleEdit","roleDelete","permissionView", "permissionCreate", "permissionEdit", "permissionDelete"]}>
                                <li className={menuState.accounts.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.accounts.active}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("accounts");
                                        }}
                                    >
                                        <MdManageAccounts size={25} className='mr-2' />
                                        <p>
                                            Accounts 
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.accounts.style}>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","userView", "userCreate", "userEdit","userDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/account/users/manage" className={`nav-link ${menu === 'users' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Users</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","roleView", "roleCreate", "roleEdit","roleDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/account/roles/manage" className={`nav-link ${menu === 'roles' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Roles</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","permissionView", "permissionCreate", "permissionEdit", "permissionDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/account/permissions" className={`nav-link ${menu === 'permissions' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Permissions</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent>

                            {/* Tool */}
                            {/* <ProtectedComponent account={account} requiredPermissions={["superAdmin","QRCodeRangeGenerator"]}>
                                <li className={menuState.tools.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.tools.active}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("tools");
                                        }}
                                    >
                                        <PiToolboxFill size={25} className='mr-2' />
                                        <p>
                                            Tools 
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.tools.style}>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","QRCodeRangeGenerator"]}>
                                            <li className="nav-item">
                                                <Link to="/tools/qr-range-generator" className={`nav-link ${menu === 'qr-range-generator' ? 'active' : ''}`}>
                                                    <p className='ml-3'>QR Range Generator</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent> */}
                            
                            {/* Reports */}
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","inventoryReport", "salesOrderReport", "invoiceReport", "paymentReceiveReport","customerReport", "shipmentReport", "purchaseOrderReport","billReport","purchaseReceiveReport", "vendorReport", "paymentMadeReport","courierReport","serviceHubReport"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.reports.active}`} to="/reports">
                                        <i className="nav-icon fas fa-chart-pie mr-2" />
                                        <p>Reports</p>
                                    </Link>
                                </li>
                            </ProtectedComponent>
                            
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","categoryView", "categoryCreate", "categoryEdit", "categoryDelete","variantTypeView", "variantTypeCreate", "variantTypeEdit","variantTypeDelete","brandView", "brandCreate", "brandEdit","brandDelete","taxRateView","taxRateCreate","taxRateEdit","taxRateDelete","discountRateView","discountRateCreate","discountRateEdit","discountRateDelete","courierView","courierCreate","courierEdit","courierDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.settings.active}`} to="/settings">
                                        <i className="nav-icon fas fa-cog mr-2" />
                                        <p>Settings</p>
                                    </Link>
                                </li>
                            </ProtectedComponent>
                        </ul> : ''
                    }
                </nav>
                
                {helpers.displayVersion()}
                {/* /.sidebar-menu */}
            </div>
            {/* /.sidebar */}
        </aside>
        </>
    )
}
